// The plans of October 2026, see docs/Kizwork-Specification-Stripe-2026-11-01.pdf
// and Meetingrooms.Plans in the backend. The prices come from Stripe (the
// planPrices query), resolved by lookup key, these are only shown while they
// load or if Stripe can't be reached.

export type PaidPlan = "starter" | "pro_security";
export type Interval = "monthly" | "yearly";

export const MAX_SEATS = 250;

// cents per seat and per month or year, excluding VAT
export const FALLBACK_PRICES: Record<string, number> = {
  starter_monthly: 799,
  starter_yearly: 7788,
  pro_security_monthly: 1499,
  pro_security_yearly: 14388,
  voice_trust_plus_monthly: 499,
  voice_trust_plus_yearly: 4788,
};

export const ENTERPRISE_CONTACT =
  "mailto:contact@kizwork.com?subject=Enterprise%20Fortress";

export const planLookupKey = (plan: PaidPlan, interval: Interval) =>
  `${plan}_${interval}`;

export const addonLookupKey = (interval: Interval) =>
  `voice_trust_plus_${interval}`;

// The plan of the API (STARTER) as the one of the lookup keys (starter)
export const paidPlanOf = (plan: Plan | undefined): PaidPlan | null =>
  plan === "STARTER"
    ? "starter"
    : plan === "PRO_SECURITY"
    ? "pro_security"
    : null;

export const intervalOf = (billingInterval?: string | null): Interval =>
  billingInterval === "year" ? "yearly" : "monthly";

// What /billing/checkout?price=...&addon=... asks for: a plan price, and the
// Voice Trust+ price of the same interval. Anything else is null, the page then
// goes back to the pricing table.
export const parseCheckout = (
  price: string | null,
  addon: string | null
): { plan: PaidPlan; interval: Interval; addon: boolean } | null => {
  const match = price?.match(/^(starter|pro_security)_(monthly|yearly)$/);
  if (!match) return null;
  const plan = match[1] as PaidPlan;
  const interval = match[2] as Interval;
  if (addon && addon !== addonLookupKey(interval)) return null;
  return { plan, interval, addon: !!addon };
};

export const checkoutPath = (
  plan: PaidPlan,
  interval: Interval,
  addon: boolean,
  organizationId?: string
) => {
  const params = new URLSearchParams({ price: planLookupKey(plan, interval) });
  if (addon) params.set("addon", addonLookupKey(interval));
  if (organizationId) params.set("org", organizationId);
  return `/billing/checkout?${params.toString()}`;
};

export const pricesByKey = (prices: PlanPrice[] | undefined) => {
  const byKey: Record<string, number> = { ...FALLBACK_PRICES };
  for (const price of prices ?? []) byKey[price.lookupKey] = price.unitAmount;
  return byKey;
};

// The amount per seat and per month: a yearly price is billed once for 12
// months
export const perMonth = (cents: number, interval: Interval) =>
  interval === "yearly" ? cents / 12 / 100 : cents / 100;

export const formatEuros = (locale: string, euros: number) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(euros);

// The Checkout chosen before logging in with a provider (Google), whose
// callback loses the page we came from: we go back to it once logged in, see
// Home.tsx
const PENDING_CHECKOUT = "pendingCheckout";

export const savePendingCheckout = (path: string) => {
  try {
    localStorage.setItem(
      PENDING_CHECKOUT,
      JSON.stringify({ path, at: Date.now() })
    );
  } catch (e) {}
};

export const takePendingCheckout = (): string | null => {
  try {
    const value = localStorage.getItem(PENDING_CHECKOUT);
    localStorage.removeItem(PENDING_CHECKOUT);
    if (!value) return null;
    const { path, at } = JSON.parse(value);
    // an hour at most
    if (Date.now() - at > 3600 * 1000) return null;
    return typeof path === "string" && path.startsWith("/billing/checkout")
      ? path
      : null;
  } catch (e) {
    return null;
  }
};

export const clearPendingCheckout = () => {
  try {
    localStorage.removeItem(PENDING_CHECKOUT);
  } catch (e) {}
};
